import { Injectable } from '@angular/core';
import { CategoryPathsCategories, CategoryPathsCategoryGroups } from '@app/category-taxonomy/models/types/category-paths-display.type';
import { CategoryPath, CategoryPathsResponse } from '@app/category-taxonomy/services/responses/category-paths.response';
import { isEmpty } from '@app/core/functions/isEmpty';

@Injectable({
    providedIn: 'root'
})
export class CategoryPathsFormatterService {

    /**
     * Formats the response from the API into a CategoryPathsCategoryGroups model.
     * If the response contains product_types, the grouping will be done by product type, then item type.
     * If the response contains paths:
     *      If 'productType' exists in the meta data, the grouping will be done by item type, then product type
     *      Otherwise paths will be grouped by their categories, which all go into one generic group.
     */
    formatCategoryPaths(response: CategoryPathsResponse): CategoryPathsCategoryGroups {
        if (response === null) {
            return [];
        }

        if ('product_types' in response) {
            return this.formatCategoriesByMetaData(response.product_types, 'itemType');
        }

        return this.formatPaths(response.paths);
    }

    private formatPaths(paths: CategoryPath[]): CategoryPathsCategoryGroups {
        if (isEmpty(paths)) {
            return [];
        }

        if (paths[0].meta && 'productType' in paths[0].meta) {
            return this.formatCategoriesByMetaData(paths, 'productType');
        }

        return [this.formatCategoriesByValue(paths)];
    }

    private formatCategoriesByMetaData(paths: CategoryPath[], key: 'productType' | 'itemType'): CategoryPathsCategoryGroups {
        if (isEmpty(paths) || !(paths[0].meta && key in paths[0].meta)) {
            return [];
        }

        const categoryGroups: CategoryPathsCategoryGroups = [];
        let previousCategories: CategoryPathsCategories = [];
        let previousPaths: CategoryPath[] = [];
        let previousCategory: string | undefined = undefined;
        let previousGroup: string | undefined = undefined;

        for (const path of paths) {
            const currentGroup: string = path.value;
            const currentCategory: string = path.meta[key];

            // If we're changing groups
            if (currentGroup !== previousGroup) {
                // We must also change categories, so go ahead and push any previous paths and categories
                if (previousPaths.length > 0) {
                    previousCategories.push(previousPaths);
                    categoryGroups.push(previousCategories);
                }

                // Reset to the new group and the new category, reset the categories, and push the current path to the list of paths
                previousGroup = currentGroup;
                previousCategory = currentCategory;
                previousCategories = [];
                previousPaths = [path];
            } else if (currentCategory !== previousCategory) {
                // Else if we're changing categories

                // And we've had a previous list of paths being built, push the paths to the category
                if (previousPaths.length > 0) {
                    previousCategories.push(previousPaths);
                }

                // Reset to the new category and push the current path to the list of paths
                previousCategory = currentCategory;
                previousPaths = [path];
            } else {
                // Otherwise nothing changed, so just push the current path to the list of paths
                previousPaths.push(path);
            }
        }

        // Finally, push the last list of paths to the categories and that list of categories to the category groups
        if (previousPaths.length > 0) {
            previousCategories.push(previousPaths);
            categoryGroups.push(previousCategories);
        }

        return categoryGroups;
    }

    /**
     * Currently, all channels that should be formatted by value only have one path per category,
     * so just expand the paths into an array of arrays, with each array containing a single path.
     */
    private formatCategoriesByValue(paths: CategoryPath[]): CategoryPathsCategories {
        return paths.map((path: CategoryPath) => [path]);
    }
}
